import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { api } from "@/lib/api";
import { MapPin, Navigation, ArrowRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1757466762489-52fea38071ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGRpZ2l0YWwlMjBkYXRhJTIwbmV0d29yayUyMGRhcmt8ZW58MHx8fHwxNzg4NDUyODI3fDA&ixlib=rb-4.1.0&q=85";

// Haversine distance in km
function haversine(a, b) {
  const R = 6371, toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat/2)**2 + Math.cos(toRad(a.lat))*Math.cos(toRad(b.lat))*Math.sin(dLng/2)**2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

export default function AtmLocator() {
  const [atms, setAtms] = useState([]);
  const [pos, setPos] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/atms").then((r)=>setAtms(r.data.atms)).catch(()=>{}); }, []);

  const locate = () => {
    if (!navigator.geolocation) return setErr("Geolocation not supported");
    setLoading(true); setErr("");
    navigator.geolocation.getCurrentPosition(
      (p) => { setPos({ lat: p.coords.latitude, lng: p.coords.longitude }); setLoading(false); },
      (e) => { setErr(e.message || "Permission denied"); setLoading(false); },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  };

  const ranked = pos
    ? atms.map((a) => ({ ...a, km: haversine(pos, a) })).sort((x,y) => x.km - y.km)
    : atms;
  const nearest = ranked[0];
  const proj = (lat, lng) => ({ left: `${((lng + 180) / 360) * 100}%`, top: `${((90 - lat) / 180) * 100}%` });

  return (
    <div className="min-h-screen text-white">
      <nav className="glass sticky top-0 z-40 border-b border-white/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" data-testid="loc-nav-logo" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#d4af37] to-[#7a5f16] grid place-items-center font-display font-black text-black">★</div>
            <div className="leading-none">
              <div className="font-display text-lg font-bold">BlackStar</div>
              <div className="font-mono text-[10px] tracking-[0.3em] text-white/60">ATM LOCATOR</div>
            </div>
          </Link>
          <Link to="/register" data-testid="loc-cta-open" className="btn-primary">Open a Vault</Link>
        </div>
      </nav>

      <section className="relative overflow-hidden grain">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20"/>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#050505]"/>
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-10">
          <div className="chip cyan mb-4">◆ CASH-REDEEMABLE · GLOBAL</div>
          <h1 className="font-display font-black leading-tight tracking-tighter" style={{fontSize:"clamp(2.5rem, 6vw, 5rem)"}}>
            Find a BlackStar ATM<br/><span className="italic text-[#d4af37]">near you.</span>
          </h1>
          <p className="text-white/70 mt-6 max-w-2xl">Every money order dispatched from a BlackStar vault is redeemable for cash at any of our partner ATMs. Share your location and we'll pin your nearest cash-out point.</p>
          <div className="mt-8 flex gap-3">
            <button data-testid="locate-btn" onClick={locate} disabled={loading} className="btn-primary inline-flex items-center gap-2">
              <Navigation size={16}/>{loading ? "Locating…" : (pos ? "Update location" : "Use my location")}
            </button>
            {pos && <span className="chip green" data-testid="pos-chip">◈ {pos.lat.toFixed(3)}, {pos.lng.toFixed(3)}</span>}
            {err && <span className="chip" style={{color:"#ff3366", borderColor:"rgba(255,51,102,0.4)"}} data-testid="pos-err">{err}</span>}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-10">
        {nearest && pos && (
          <div className="panel p-6 mb-8 border-[#d4af37]/40" data-testid="nearest-atm">
            <div className="chip gold mb-3">NEAREST CASH-OUT</div>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="font-display text-3xl">{nearest.city}, {nearest.country}</div>
                <div className="font-mono text-xs text-white/50 mt-1">{nearest.id} · {nearest.network} · {nearest.currencies.join(" · ")}</div>
              </div>
              <div className="text-right">
                <div className="font-display text-4xl text-[#39ff14]">{nearest.km.toFixed(0)} km</div>
                <Link
                  to={`/app/send?atm=${encodeURIComponent(nearest.id)}&country=${nearest.country}`}
                  data-testid="prefill-send-link"
                  className="btn-primary inline-flex items-center gap-2 mt-3"
                >Send a Money Order Here <ArrowRight size={16}/></Link>
              </div>
            </div>
          </div>
        )}

        <div className="panel p-4 relative overflow-hidden mb-8" style={{aspectRatio:"2/1"}} data-testid="locator-map">
          <svg viewBox="0 0 360 180" preserveAspectRatio="none" className="absolute inset-0 w-full h-full opacity-25">
            {Array.from({length: 18}).map((_,i)=>(<line key={"h"+i} x1="0" y1={i*10} x2="360" y2={i*10} stroke="#00f0ff" strokeWidth="0.15"/>))}
            {Array.from({length: 36}).map((_,i)=>(<line key={"v"+i} x1={i*10} y1="0" x2={i*10} y2="180" stroke="#00f0ff" strokeWidth="0.15"/>))}
          </svg>
          {pos && (
            <div className="absolute -translate-x-1/2 -translate-y-1/2" style={proj(pos.lat, pos.lng)}>
              <div className="w-3 h-3 rounded-full bg-[#00f0ff] shadow-[0_0_16px_#00f0ff] animate-ping absolute"/>
              <div className="w-3 h-3 rounded-full bg-[#00f0ff] shadow-[0_0_16px_#00f0ff] relative"/>
            </div>
          )}
          {ranked.map((a, i) => (
            <div key={a.id} className="absolute -translate-x-1/2 -translate-y-1/2 group" style={proj(a.lat, a.lng)}>
              <div className={`rounded-full ${i===0 && pos ? "w-3 h-3 bg-[#d4af37] shadow-[0_0_14px_#d4af37]" : "w-2 h-2 bg-[#39ff14] shadow-[0_0_10px_#39ff14]"}`}/>
              <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute left-3 top-0 whitespace-nowrap font-mono text-[10px] bg-black/80 border border-white/10 px-2 py-1 rounded">
                {a.city}{pos ? ` · ${a.km.toFixed(0)}km` : ""}
              </div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-3">
          {ranked.slice(0, 12).map((a) => (
            <div key={a.id} className="panel p-4" data-testid={`loc-atm-${a.id}`}>
              <div className="flex justify-between items-center">
                <span className="font-mono text-xs text-[#d4af37]">{a.id}</span>
                {pos && <span className="font-mono text-xs text-[#39ff14]">{a.km.toFixed(0)} km</span>}
              </div>
              <div className="font-display text-lg mt-2 flex items-center gap-2"><MapPin size={14}/> {a.city}, {a.country}</div>
              <div className="text-xs text-white/50 mt-1 font-mono">{a.network} · {a.currencies.join(" · ")}</div>
            </div>
          ))}
        </div>
      </section>
      <footer className="border-t border-white/10 py-8 mt-6 text-center text-xs text-white/40 font-mono">© 2026 BlackStar Virtual Mint · Cash-redeemable everywhere</footer>
    </div>
  );
}
